/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import { Configuration, FetchAPI } from "./backend/src"
import { getAccessToken, getBasePath } from "./url"
import { isElectronApiAvailable } from "./electronAdapter"
import { authStorageAdapter } from "../auth/storageAdapter"

export const openApiFetch: FetchAPI = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const url =
    typeof input === "string"
      ? input
      : typeof (input as any)?.url === "string"
        ? (input as any).url
        : input.toString()

  if (isElectronApiAvailable()) {
    let fullUrl = url
    if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
      const defaultBaseUrl =
        process.env.NEXT_PUBLIC_SALES_API ||
        process.env.VITE_SALES_API ||
        "http://localhost:8101"
      fullUrl = `${defaultBaseUrl.replace(/\/+$/, "")}/${fullUrl.replace(/^\/+/, "")}`
    }

    const headers: Record<string, string> = {}
    if (init?.headers) {
      if (typeof (init.headers as any).forEach === "function") {
        ;(init.headers as any).forEach((val: string, key: string) => {
          headers[key] = String(val)
        })
      } else if (Array.isArray(init.headers)) {
        for (const [key, val] of init.headers) {
          headers[key] = String(val)
        }
      } else {
        for (const [key, val] of Object.entries(init.headers)) {
          if (val !== undefined && val !== null) {
            headers[key] = String(val)
          }
        }
      }
    }

    // Attach Bearer token if not present
    const token = await authStorageAdapter.getToken()
    if (token && typeof token === "string" && token.startsWith("ory_st_")) {
      const hasAuth = Object.keys(headers).some(
        (k) => k.toLowerCase() === "authorization"
      )
      if (!hasAuth) {
        headers["Authorization"] = `Bearer ${token}`
      }
    }

    let data: any = init?.body
    if (typeof data === "string") {
      try {
        data = JSON.parse(data)
      } catch {
        // keep string
      }
    }

    const ipcResponse = await window.electron!.apiRequest({
      method: (init?.method || "GET").toUpperCase(),
      url: fullUrl,
      headers,
      data,
    })

    if (ipcResponse.status === 401) {
      console.warn(
        "[OpenAPI Client]: 401 Unauthorized via Electron IPC, resetting session"
      )
      await authStorageAdapter.clearToken()
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("auth:session-expired"))
      }
    }

    const isNullBodyStatus = [101, 204, 205, 304].includes(ipcResponse.status)
    const body = isNullBodyStatus
      ? null
      : ipcResponse.data !== undefined && ipcResponse.data !== null
        ? typeof ipcResponse.data === "string"
          ? ipcResponse.data
          : JSON.stringify(ipcResponse.data)
        : ""

    return new Response(body, {
      status: ipcResponse.status || 200,
      statusText: ipcResponse.statusText || "OK",
      headers: (ipcResponse.headers as any) || {},
    })
  }

  return fetch(input, {
    ...init,
    credentials: init?.credentials || "include",
  })
}

export function createConfiguration(): Configuration {
  const token = getAccessToken()
  return new Configuration({
    basePath: getBasePath(),
    credentials: "include",
    accessToken: token && token.startsWith("ory_st_") ? token : undefined,
    fetchApi: openApiFetch,
  })
}
